import React, { useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Shield, Phone, X } from 'lucide-react';
import { companyInfo } from '../mock';
import { Card, CardContent } from './ui/card';

// License data structure - ready for verified data from Gabriel
// Currently empty - will be populated with verified license information
const licenseData = {
  // Example structure (not to be shown publicly until data is provided):
  // "TX": {
  //   name: "Texas",
  //   abbreviation: "TX",
  //   licenseNumber: "XXXXX",
  //   holder: "Agency/Producer Name",
  //   validity: "Active through MM/DD/YYYY",
  //   verificationSource: "State Department of Insurance",
  //   verificationDate: "MM/DD/YYYY"
  // }
};

const ServiceStates = () => {
  const { language } = useLanguage();
  const [selectedState, setSelectedState] = useState(null);
  const [hoveredState, setHoveredState] = useState(null);

  const content = {
    en: {
      title: "Where We Are Licensed",
      subtitle: "Ask us about availability in your state",
      description: "We are working to serve clients across multiple states. Contact us to confirm current licensing status and product availability in your jurisdiction.",
      legend: {
        verified: "Verified License",
        askAbout: "Ask About Availability"
      },
      noDataYet: "License verification in progress. Please contact us to confirm our current licensing status in your state.",
      contactPrompt: "Questions about licensing or availability?",
      callUs: "Call us at",
      note: "Note: Licensing status is distinct from product and carrier availability, which may vary by state and insurance company.",
      clickForInfo: "Click state for license details (when available)"
    },
    es: {
      title: "Dónde Estamos Licenciados",
      subtitle: "Consúltanos sobre la disponibilidad en tu estado",
      description: "Estamos trabajando para servir a clientes en múltiples estados. Contáctenos para confirmar el estado actual de licencias y disponibilidad de productos en su jurisdicción.",
      legend: {
        verified: "Licencia Verificada",
        askAbout: "Consulte Disponibilidad"
      },
      noDataYet: "Verificación de licencias en progreso. Por favor contáctenos para confirmar nuestro estado de licencias actual en su estado.",
      contactPrompt: "¿Preguntas sobre licencias o disponibilidad?",
      callUs: "Llámenos al",
      note: "Nota: El estado de licencias es distinto de la disponibilidad de productos y aseguradoras, que puede variar según el estado y la compañía de seguros.",
      clickForInfo: "Haga clic en el estado para detalles de licencia (cuando esté disponible)"
    }
  };

  const text = content[language];

  // Check if state has verified license data
  const hasLicenseData = (stateAbbr) => {
    return licenseData[stateAbbr] && licenseData[stateAbbr].licenseNumber;
  };

  const handleStateClick = (stateAbbr, stateName) => {
    if (hasLicenseData(stateAbbr)) {
      setSelectedState({ abbr: stateAbbr, name: stateName, data: licenseData[stateAbbr] });
    }
  };

  // Get state color - neutral gray for all states until data is provided
  const getStateColor = (stateAbbr) => {
    if (hasLicenseData(stateAbbr)) {
      return hoveredState === stateAbbr ? "#1e40af" : "#3b82f6"; // Blue for verified
    }
    return hoveredState === stateAbbr ? "#9ca3af" : "#d1d5db"; // Neutral gray
  };

  const getStateFill = (stateAbbr) => {
    return hasLicenseData(stateAbbr) ? "#3b82f6" : "#d1d5db";
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <div className="inline-block bg-blue-100 p-3 rounded-full mb-4">
            <Shield className="w-8 h-8 text-blue-700" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {text.title}
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-2">
            {text.subtitle}
          </p>
          <p className="text-base text-gray-500 max-w-2xl mx-auto">
            {text.description}
          </p>
        </div>

        {/* Legend */}
        <div className="flex justify-center gap-6 mb-8 flex-wrap">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-blue-500 rounded"></div>
            <span className="text-sm text-gray-700">{text.legend.verified}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 bg-gray-300 rounded"></div>
            <span className="text-sm text-gray-700">{text.legend.askAbout}</span>
          </div>
        </div>

        {/* Map Container */}
        <div className="max-w-5xl mx-auto mb-8">
          <Card className="shadow-xl border-none">
            <CardContent className="p-4 md:p-8">
              {/* US Map SVG - Simplified version with all states, DC, Alaska, Hawaii, and Puerto Rico */}
              <div className="relative">
                <svg
                  viewBox="0 0 960 600"
                  className="w-full h-auto"
                  role="img"
                  aria-label={text.title}
                >
                  {/* Main US Map Group */}
                  <g id="mainland">
                    {/* Note: Full SVG paths for all 50 states would go here */}
                    {/* For brevity, showing structure with a few example states */}
                    {/* Each state would have: data-state attribute, onClick, onMouseEnter, onMouseLeave */}
                    
                    {/* Texas - Example */}
                    <path
                      d="M520 380 L580 380 L580 450 L520 450 Z"
                      fill={getStateFill("TX")}
                      stroke="#fff"
                      strokeWidth="1.5"
                      data-state="TX"
                      onClick={() => handleStateClick("TX", "Texas")}
                      onMouseEnter={() => setHoveredState("TX")}
                      onMouseLeave={() => setHoveredState(null)}
                      className="cursor-pointer transition-all duration-200"
                      role="button"
                      tabIndex="0"
                      aria-label="Texas"
                    />
                    
                    {/* Placeholder text indicating map structure is ready */}
                    <text x="480" y="300" fontSize="14" fill="#666" textAnchor="middle">
                      {language === 'en' ? 'Full US Map SVG' : 'Mapa completo SVG de EE.UU.'}
                    </text>
                    <text x="480" y="320" fontSize="12" fill="#999" textAnchor="middle">
                      {language === 'en' ? '50 States + DC' : '50 Estados + DC'}
                    </text>
                  </g>

                  {/* Alaska Inset */}
                  <g id="alaska" transform="translate(50, 480)">
                    <rect x="0" y="0" width="100" height="80" fill={getStateFill("AK")} stroke="#fff" strokeWidth="1.5" />
                    <text x="50" y="45" fontSize="12" fill="#333" textAnchor="middle">AK</text>
                  </g>

                  {/* Hawaii Inset */}
                  <g id="hawaii" transform="translate(200, 500)">
                    <rect x="0" y="0" width="80" height="60" fill={getStateFill("HI")} stroke="#fff" strokeWidth="1.5" />
                    <text x="40" y="35" fontSize="12" fill="#333" textAnchor="middle">HI</text>
                  </g>

                  {/* Puerto Rico Inset */}
                  <g id="puerto-rico" transform="translate(750, 500)">
                    <rect x="0" y="0" width="90" height="40" fill={getStateFill("PR")} stroke="#fff" strokeWidth="1.5" />
                    <text x="45" y="25" fontSize="12" fill="#333" textAnchor="middle">PR</text>
                  </g>
                </svg>
                
                <p className="text-center text-sm text-gray-500 mt-4">
                  {text.clickForInfo}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* No Data Message */}
        <div className="max-w-3xl mx-auto mb-8">
          <Card className="border-l-4 border-blue-500">
            <CardContent className="p-6">
              <p className="text-gray-700 mb-4">
                {text.noDataYet}
              </p>
              <p className="text-sm text-gray-600 italic">
                {text.note}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Contact CTA */}
        <div className="text-center">
          <p className="text-lg text-gray-700 mb-4">{text.contactPrompt}</p>
          <a
            href={`tel:${companyInfo.phone}`}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all shadow-lg"
          >
            <Phone className="w-5 h-5" />
            <span>{text.callUs} {companyInfo.phone}</span>
          </a>
        </div>

        {/* License Detail Modal */}
        {selectedState && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedState(null)}
          >
            <Card className="max-w-md w-full shadow-2xl" onClick={(e) => e.stopPropagation()}>
              <CardContent className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                      {selectedState.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {language === 'en' ? 'License Information' : 'Información de Licencia'}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedState(null)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label="Close"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
                
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600">{language === 'en' ? 'License Number:' : 'Número de Licencia:'}</p>
                    <p className="font-semibold">{selectedState.data.licenseNumber}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{language === 'en' ? 'Holder:' : 'Titular:'}</p>
                    <p className="font-semibold">{selectedState.data.holder}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">{language === 'en' ? 'Validity:' : 'Vigencia:'}</p>
                    <p className="font-semibold">{selectedState.data.validity}</p>
                  </div>
                  <div className="pt-3 border-t">
                    <p className="text-xs text-gray-500">
                      {language === 'en' ? 'Verified:' : 'Verificado:'} {selectedState.data.verificationDate}
                    </p>
                    <p className="text-xs text-gray-500">
                      {language === 'en' ? 'Source:' : 'Fuente:'} {selectedState.data.verificationSource}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </section>
  );
};

export default ServiceStates;
